/**
 * Settings Store
 *
 * Caches all 9-step Settings wizard data to avoid redundant API calls.
 * Each section tracks its own loading flag and loaded state.
 * Views should call the `ensure*` actions — these skip the network
 * request when data is already in the store.  Call `invalidate(section)`
 * after a successful mutation to force a reload on next access.
 */
import { defineStore } from 'pinia';

// ── Wizard step progress ──────────────────────────────────────────────────────
// The localStorage cache lives in its own module so auth.store.ts can clear it
// synchronously without a circular import.  Re-exported here because this has
// been the public import path for these helpers since round 1.
import {
  readCompletedSteps,
  writeCompletedSteps,
  readOnboardingStarted,
  writeOnboardingStarted,
  clearOnboardingProgress,
  writeIsSettingsConfigured,
  resolveIsSettingsConfigured, // LOCAL_ONBOARDING_OVERRIDE: revert to an inline localStorage/JWT chain using readIsSettingsConfigured()
} from '@/modules/settings/onboardingProgress';

export {
  readCompletedSteps,
  writeCompletedSteps,
  readOnboardingStarted,
  writeOnboardingStarted,
  clearOnboardingProgress,
};
import { isAxiosError } from 'axios';
import { logAuditEvent } from '@/core/services/audit.service';
import { computeDiff } from '@tms-phoenix/ui-vue';
import { fetchAvailableLanguages, fetchLocaleMap } from '@/core/services/locale.service';
import { applyFreshBundle } from '@/core/plugins/i18n';
import type { TranslationBundle } from '@tms-phoenix/tms-types';
import {
  fetchLocaleKeys,
  saveOverrides,
  deleteOverride,
  resetAllOverrides,
  importOverrides,
  syncBundle,
  previewReplace,
  applyReplace,
} from '@/core/services/locale-admin.service';
import type { LocaleSyncBody } from '@/core/services/locale-admin.service';
import type {
  LocaleKeysQuery,
  LocaleReplaceApplyBody,
  LocaleReplacePreviewBody,
} from '@tms-phoenix/tms-types';
import { useAuthStore } from '@/stores/auth.store';
import { useEntitlementsStore } from '@/stores/entitlements.store';
import type { Language, LocaleKey, Device } from '@tms-phoenix/tms-types';
import { config } from '@/core/config';
import {
  fetchAllergenManagement,
  updateAllergenAction,
  markSettingsConfigured as markSettingsConfiguredApi,
  fetchLocalizationSettings,
  saveLocalizationSettings,
  fetchMilkTypes,
  updateMilkTypeStatus,
  updateMilkTypeSeverity,
  fetchHospitalConfig,
  saveHospitalConfig,
  fetchFacilities,
  addFacility,
  updateFacility,
  fetchUnitConfigs,
  addUnitConfig,
  updateUnitConfig,
  fetchExpiryRules,
  addExpiryRule,
  updateExpiryRule,
  deleteExpiryRule,
  setExpiryRuleActive,
  fetchRolesPermissions,
  fetchRolesPermissionsDefaults,
  saveRolesPermissions,
  fetchDevices,
  updateDeviceCareUnit,
  fetchApprovalCompliance,
  saveApprovalCompliance,
  reEvaluateDonorScreening,
  fetchMilkFormulaStorage,
  saveMilkFormulaStorage,
  listIdps,
  createIdp,
  updateIdp,
  deleteIdp,
  addOnboardingStepApi,
  fetchBridgelinkAssets,
} from '@/modules/settings/services/settings.api';
import type { IdpProviderResponse, CreateIdpPayload, UpdateIdpPayload } from '@/modules/settings/services/settings.api';
import type { BridgelinkAssetsResponse } from '@tms-phoenix/tms-types';
import type {
  AllergenManagementEntry,
  AllergenAction,
  LocalizationFormData,
  MilkTypeEntry,
  HospitalConfigFormData,
  FacilityEntry,
  UnitConfigEntry,
  ExpiryRuleEntry,
  RolePermissionEntry,
  ApprovalComplianceFormData,
  MilkFormulaStorageFormData,
} from '@/modules/settings/types';

type SectionKey =
  | 'allergens'
  | 'localization'
  | 'milkTypes'
  | 'hospitalConfig'
  | 'facilities'
  | 'unitConfigs'
  | 'expiryRules'
  | 'feedExpiryRules'
  | 'formulaExpiryRules'
  | 'rolesPermissions'
  | 'devices'
  | 'approvalCompliance'
  | 'milkFormulaStorage'
  | 'idpFederation'
  | 'localeLanguages'
  | 'localeKeys';

interface SettingsState {
  /** null = not yet checked; true = all settings configured; false = first-time setup */
  settingsOnboarded: boolean | null;

  /**
   * Step keys the user has **successfully saved** during this first-time setup
   * session. Advanced by calling `completeStep(key)` from the step view after a
   * successful save — never on a bare route visit. Steps can be completed in any
   * order; the router guard allows free navigation to any of the 10 step paths.
   * Only meaningful when `settingsOnboarded === false`.
   */
  completedSteps: string[];

  /**
   * Whether the admin has clicked past the welcome splash — independent of
   * `completedSteps`, since clicking "Begin Configuration" and completing
   * zero steps should still show the checklist (not the splash) on the next
   * visit. Only meaningful when `settingsOnboarded === false`.
   */
  onboardingStarted: boolean;

  allergens: AllergenManagementEntry[];
  allergensFetched: boolean;
  allergensLoading: boolean;

  localization: LocalizationFormData | null;
  localizationFetched: boolean;
  localizationLoading: boolean;

  milkTypes: MilkTypeEntry[];
  milkTypesFetched: boolean;
  milkTypesLoading: boolean;

  hospitalConfig: HospitalConfigFormData | null;
  hospitalConfigFetched: boolean;
  hospitalConfigLoading: boolean;

  facilities: FacilityEntry[];
  facilitiesFetched: boolean;
  facilitiesLoading: boolean;

  unitConfigs: UnitConfigEntry[];
  unitConfigsFetched: boolean;
  unitConfigsLoading: boolean;

  expiryRules: ExpiryRuleEntry[];
  expiryRulesFetched: boolean;
  expiryRulesLoading: boolean;

  feedExpiryRules: ExpiryRuleEntry[];
  feedExpiryRulesFetched: boolean;
  feedExpiryRulesLoading: boolean;

  formulaExpiryRules: ExpiryRuleEntry[];
  formulaExpiryRulesFetched: boolean;
  formulaExpiryRulesLoading: boolean;

  rolesPermissions: RolePermissionEntry[];
  rolesPermissionsFetched: boolean;
  rolesPermissionsLoading: boolean;
  rolesPermissionsDefaults: RolePermissionEntry[];
  rolesPermissionsDefaultsFetched: boolean;

  devices: Device[];
  devicesFetched: boolean;
  devicesFetchedType: string | undefined;
  devicesLoading: boolean;

  approvalCompliance: ApprovalComplianceFormData | null;
  approvalComplianceFetched: boolean;
  approvalComplianceLoading: boolean;

  milkFormulaStorage: MilkFormulaStorageFormData | null;
  milkFormulaStorageFetched: boolean;
  milkFormulaStorageLoading: boolean;

  idpFederation: IdpProviderResponse[];
  idpFederationFetched: boolean;
  idpFederationLoading: boolean;

  // There's no separate entitlement for interoperability — bridgelink_enabled
  // (from GET /assets) is the sole source of truth for whether the
  // interoperability-config step/card/route exists at all.
  bridgelinkAssets: BridgelinkAssetsResponse | null;
  bridgelinkAssetsFetched: boolean;
  bridgelinkAssetsLoading: boolean;

  localeLanguages: Language[];
  localeLanguagesFetched: boolean;
  localeLanguagesLoading: boolean;

  localeKeys: LocaleKey[];
  localeKeysFetched: boolean;
  localeKeysLoading: boolean;

  localeKeysPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  localeKeysQuery: LocaleKeysQuery;
  /** How many keys this hospital has customized — drives the Localization page's "Customized" filter button. */
  customizedLocaleKeysCount: number;

  localeMap: TranslationBundle | null;
  localeMapFetched: boolean;
  localeMapLoading: boolean;
}

/**
 * Returns true when the current tenant's plan and entitlements unlock the
 * formula-storage (inventory-locations) section.
 *
 * Centralised here so that `ensureMilkFormulaStorage` and
 * `saveMilkFormulaStorage` can never drift out of sync with each other.
 *
 * @param override - when explicitly provided, bypasses the entitlement check
 *   (useful for callers that already know the value, e.g. tests).
 */
function resolveShouldIncludeFormula(override?: boolean): boolean {
  if (override !== undefined) return override;
  const entitlements = useEntitlementsStore();
  return entitlements.hasEntitlement('INVENTORY') && entitlements.planFlag === 'P3';
}

/**
 * Diff two role-permission matrices down to just the roles/modules that actually
 * changed. Embedding the FULL matrix (all roles x all modules) in both oldValue
 * and newValue made this audit event's body large enough to trip the ALB's WAF
 * managed rules (403, before the request ever reached the backend) — see
 * EXCLUDED_PATH_PREFIXES in modules/tms-infra/lib/tenant/waf-rules.ts for other
 * routes that hit the same issue with "rich" POST bodies.
 */
function computeRolePermissionsDiff(
  oldRoles: RolePermissionEntry[],
  newRoles: RolePermissionEntry[],
): { oldValue: Record<string, unknown>; newValue: Record<string, unknown> } {
  const oldById = new Map(oldRoles.map((r) => [r.id, r.permissions]));
  const mutatedRoles: string[] = [];
  const oldDiff: Record<string, Partial<RolePermissionEntry['permissions']>> = {};
  const newDiff: Record<string, Partial<RolePermissionEntry['permissions']>> = {};

  for (const role of newRoles) {
    const oldPermissions = oldById.get(role.id);
    const oldModuleDiff: Partial<RolePermissionEntry['permissions']> = {};
    const newModuleDiff: Partial<RolePermissionEntry['permissions']> = {};

    for (const moduleKey of Object.keys(role.permissions) as (keyof RolePermissionEntry['permissions'])[]) {
      const oldVal = oldPermissions?.[moduleKey];
      const newVal = role.permissions[moduleKey];
      if (oldVal !== newVal) {
        oldModuleDiff[moduleKey] = oldVal ?? false;
        newModuleDiff[moduleKey] = newVal;
      }
    }

    if (Object.keys(newModuleDiff).length > 0) {
      mutatedRoles.push(role.id);
      oldDiff[role.id] = oldModuleDiff;
      newDiff[role.id] = newModuleDiff;
    }
  }

  return {
    oldValue: { mutatedFields: mutatedRoles, roleModuleMatrix: oldDiff },
    newValue: { mutatedFields: mutatedRoles, roleModuleMatrix: newDiff },
  };
}

export const useSettingsStore = defineStore('settings', {
  state: (): SettingsState => ({
    settingsOnboarded: null,
    completedSteps: [],
    onboardingStarted: false,

    allergens: [],
    allergensFetched: false,
    allergensLoading: false,

    localization: null,
    localizationFetched: false,
    localizationLoading: false,

    milkTypes: [],
    milkTypesFetched: false,
    milkTypesLoading: false,

    hospitalConfig: null,
    hospitalConfigFetched: false,
    hospitalConfigLoading: false,

    facilities: [],
    facilitiesFetched: false,
    facilitiesLoading: false,

    unitConfigs: [],
    unitConfigsFetched: false,
    unitConfigsLoading: false,

    expiryRules: [],
    expiryRulesFetched: false,
    expiryRulesLoading: false,

    feedExpiryRules: [],
    feedExpiryRulesFetched: false,
    feedExpiryRulesLoading: false,

    formulaExpiryRules: [],
    formulaExpiryRulesFetched: false,
    formulaExpiryRulesLoading: false,

    rolesPermissions: [],
    rolesPermissionsFetched: false,
    rolesPermissionsLoading: false,
    rolesPermissionsDefaults: [],
    rolesPermissionsDefaultsFetched: false,

    devices: [],
    devicesFetched: false,
    devicesFetchedType: undefined,
    devicesLoading: false,

    approvalCompliance: null,
    approvalComplianceFetched: false,
    approvalComplianceLoading: false,

    milkFormulaStorage: null,
    milkFormulaStorageFetched: false,
    milkFormulaStorageLoading: false,

    idpFederation: [],
    idpFederationFetched: false,
    idpFederationLoading: false,

    bridgelinkAssets: null,
    bridgelinkAssetsFetched: false,
    bridgelinkAssetsLoading: false,

    localeLanguages: [],
    localeLanguagesFetched: false,
    localeLanguagesLoading: false,

    localeKeys: [],
    localeKeysFetched: false,
    localeKeysLoading: false,

    localeKeysPagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
    localeKeysQuery: {},
    customizedLocaleKeysCount: 0,

    localeMap: null,
    localeMapFetched: false,
    localeMapLoading: false,
  }),

  actions: {
    /** Mark a section as stale so the next `ensure*` call re-fetches. */
    invalidate(section: SectionKey) {
      (this as unknown as Record<string, boolean>)[`${section}Fetched`] = false;
    },

    // ── Allergen Management ───────────────────────────────────────────────

    async ensureAllergens() {
      if (this.allergensFetched) return;
      this.allergensLoading = true;
      try {
        this.allergens = await fetchAllergenManagement();
        this.allergensFetched = true;
      } finally {
        this.allergensLoading = false;
      }
    },

    async updateAllergenAction(id: string, action: AllergenAction) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.allergens.find((a) => a.id === id);
      if (existing) auditOldValue = { mutatedFields: ['system_action'], system_action: existing.action };
      try {
        await updateAllergenAction(id, action);
        if (existing) existing.action = action;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_ALLERGEN_ACTION_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update allergen action';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'hospitalAllergenConfig',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: { mutatedFields: ['system_action'], system_action: action },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    /**
     * Set the settings-onboarded flag from the auth store or localStorage.
     * No GET API call is made — the value comes exclusively from the
     * `is_settings_configured` field returned by GET /users/:username at
     * login time (see auth.store.ts).
     * Kept as an action so callers can await it and react to the result.
     */
    async checkSettingsOnboarding(): Promise<boolean> {
      // Delegates to the shared resolver (onboardingProgress.ts) so this and
      // settingsOnboardingGuard's fallback chain can never drift apart.
      // Unresolved (neither cache, auth store value, nor — if the local
      // override is active — the API) defaults to configured, so the wizard
      // is never incorrectly triggered by a missing signal.
      const authStore = useAuthStore();
      const resolved = await resolveIsSettingsConfigured(authStore.isSettingsConfigured);
      const configured = resolved ?? true;
      this.resolveOnboardingState(configured);
      return configured;
    },

    /**
     * Apply a resolved `is_settings_configured` value to the store.
     *
     * Single implementation shared by `checkSettingsOnboarding()` and
     * `settingsOnboardingGuard`, so both branches of the resolution can never
     * drift apart.  Deliberately one-directional: `is_settings_configured` is
     * the authority, and the progress attributes are only ever read when it is
     * false — when it is true they are actively discarded (self-healing), never
     * used to second-guess it.
     */
    resolveOnboardingState(configured: boolean) {
      this.settingsOnboarded = configured;
      if (configured) {
        this.completedSteps = [];
        this.onboardingStarted = false;
        clearOnboardingProgress();
      } else {
        this.hydrateOnboardingProgress();
      }
    },

    /**
     * Pull cached onboarding progress out of localStorage into the store.
     *
     * Safe to call repeatedly — that is the point.  The post-login
     * `fetchOnboardingProgress` in `auth.store.ts` resolves asynchronously and
     * would otherwise lose the race against the guard's one-shot resolution,
     * leaving a cross-device resume stranded until a full page reload.
     */
    hydrateOnboardingProgress() {
      this.completedSteps = readCompletedSteps();
      this.onboardingStarted = readOnboardingStarted();
    },

    /**
     * Mark the welcome splash as dismissed — called when the admin clicks
     * "Begin Configuration". Persists locally (synchronous, for the guard/view
     * to read immediately) and best-effort to the backend for cross-device
     * resume, mirroring `completeStep()`.
     */
    startOnboarding() {
      if (this.onboardingStarted) return;
      this.onboardingStarted = true;
      writeOnboardingStarted(true);
      addOnboardingStepApi({ started: true }).catch(() => { /* non-fatal */ });
    },

    /**
     * Call PUT /api/v1/tenant-settings/{hospital_id}/settings-configured with
     * `is_settings_configured: true`.  Called after the user finishes the last
     * wizard step.
     */
    async markSettingsConfigured(): Promise<void> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        await markSettingsConfiguredApi();
        this.settingsOnboarded = true;
        this.completedSteps = [];
        this.onboardingStarted = false;
        writeIsSettingsConfigured(true);
        // Wizard fully complete — progress tracking is no longer needed, and
        // leaving it behind risks a stale read overriding the configured flag.
        clearOnboardingProgress();
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'MARK_SETTINGS_CONFIGURED_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to mark settings configured';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'tenantSettings',
            isPhiAccess: false,
            newValue: { mutatedFields: ['is_settings_configured'], is_settings_configured: true },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    /**
     * Mark `key` as completed. Called by each step view via the injected
     * completion callback after a successful save — not on every route change.
     * Persists progress locally (for the guard's synchronous read) and
     * best-effort to the backend (for cross-device resume). Does NOT
     * auto-finish setup even once every required step is done — the admin
     * must explicitly click "Finish Setup" on the checklist (see
     * `SettingsWelcomeView.vue`), which calls `markSettingsConfigured()`
     * directly.
     */
    completeStep(key: string) {
      if (this.completedSteps.includes(key)) return;
      this.completedSteps = [...this.completedSteps, key];
      if (this.settingsOnboarded === false) {
        writeCompletedSteps(this.completedSteps);
        addOnboardingStepApi({ stepKey: key }).catch(() => { /* non-fatal — localStorage remains authoritative for this session */ });
      }
    },

    // ── Localization ──────────────────────────────────────────────────────

    async ensureLocalization() {
      if (this.localizationFetched) return;
      this.localizationLoading = true;
      try {
        this.localization = await fetchLocalizationSettings();
        this.localizationFetched = true;
      } finally {
        this.localizationLoading = false;
      }
    },

    async saveLocalization(data: LocalizationFormData) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      if (this.localization) {
        const diff = computeDiff(
          this.localization as unknown as Record<string, unknown>,
          data as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      }
      try {
        await saveLocalizationSettings(data);
        this.localization = data;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'SAVE_LOCALIZATION_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to save localization';
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'localization',
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: (auditNewValue ?? data) as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Milk Types ────────────────────────────────────────────────────────

    async ensureMilkTypes() {
      if (this.milkTypesFetched || this.milkTypesLoading) return;
      this.milkTypesLoading = true;
      try {
        this.milkTypes = await fetchMilkTypes();
        this.milkTypesFetched = true;
      } finally {
        this.milkTypesLoading = false;
      }
    },

    async updateMilkTypeStatus(id: string, status: MilkTypeEntry['status']) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.milkTypes.find((m) => m.id === id);
      if (existing) auditOldValue = { is_active: existing.status === 'Active' };
      try {
        await updateMilkTypeStatus(id, status);
        if (existing) existing.status = status;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_MILK_TYPE_STATUS_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update milk type status';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'STATUS_CHANGE',
            eventStatus: auditStatus,
            resourceType: 'milkTypes',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: { is_active: status === 'Active' },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async updateMilkTypeSeverity(id: string, severity: number) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.milkTypes.find((m) => m.id === id);
      if (existing) auditOldValue = { mutatedFields: ['severity_hours'], severity_hours: existing.severity ?? null };
      try {
        await updateMilkTypeSeverity(id, severity);
        if (existing) existing.severity = severity;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_MILK_TYPE_SEVERITY_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update milk type severity';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'milkTypes',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: { mutatedFields: ['severity_hours'], severity_hours: severity },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Hospital Config ───────────────────────────────────────────────────

    async ensureHospitalConfig() {
      if (this.hospitalConfigFetched) return;
      this.hospitalConfigLoading = true;
      try {
        this.hospitalConfig = await fetchHospitalConfig();
        this.hospitalConfigFetched = true;
      } finally {
        this.hospitalConfigLoading = false;
      }
    },

    async saveHospitalConfig(data: HospitalConfigFormData) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      if (this.hospitalConfig) {
        const diff = computeDiff(
          this.hospitalConfig as unknown as Record<string, unknown>,
          data as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      }
      try {
        await saveHospitalConfig(data);
        this.hospitalConfig = data;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'SAVE_HOSPITAL_CONFIG_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to save hospital config';
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'hospitalConfig',
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: (auditNewValue ?? data) as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Facilities ────────────────────────────────────────────────────────

    async ensureFacilities() {
      if (this.facilitiesFetched) return;
      this.facilitiesLoading = true;
      try {
        this.facilities = await fetchFacilities();
        this.facilitiesFetched = true;
      } finally {
        this.facilitiesLoading = false;
      }
    },

    async addFacility(entry: Omit<FacilityEntry, 'id'>) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const created = await addFacility(entry);
        this.facilities.push(created);
        auditStatus = 'SUCCESS';
        auditResourceId = created.id;
        return created;
      } catch (err) {
        auditErrorCode = 'ADD_FACILITY_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to add facility';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'CREATE',
            eventStatus: auditStatus,
            resourceType: 'facilities',
            resourceId: auditResourceId,
            isPhiAccess: false,
            newValue: entry as unknown as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async updateFacility(entry: FacilityEntry) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.facilities.find((f) => f.id === entry.id);
      if (existing) {
        const diff = computeDiff(
          existing as unknown as Record<string, unknown>,
          entry as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      } else {
        auditNewValue = entry as unknown as Record<string, unknown>;
      }
      try {
        await updateFacility(entry);
        const idx = this.facilities.findIndex((f) => f.id === entry.id);
        if (idx !== -1) this.facilities[idx] = entry;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_FACILITY_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update facility';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'facilities',
            resourceId: entry.id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Unit Configs ──────────────────────────────────────────────────────

    async ensureUnitConfigs() {
      if (this.unitConfigsFetched) return;
      this.unitConfigsLoading = true;
      try {
        this.unitConfigs = await fetchUnitConfigs();
        this.unitConfigsFetched = true;
      } finally {
        this.unitConfigsLoading = false;
      }
    },

    async addUnitConfig(entry: Omit<UnitConfigEntry, 'id'>, facilityId: string) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const created = await addUnitConfig(entry, facilityId);
        this.unitConfigs.push(created);
        auditStatus = 'SUCCESS';
        auditResourceId = created.id;
        return created;
      } catch (err) {
        auditErrorCode = 'ADD_UNIT_CONFIG_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to add unit config';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'CREATE',
            eventStatus: auditStatus,
            resourceType: 'careUnits',
            resourceId: auditResourceId,
            isPhiAccess: false,
            newValue: { ...entry, facilityId } as unknown as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async updateUnitConfig(entry: UnitConfigEntry) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.unitConfigs.find((u) => u.id === entry.id);
      if (existing) {
        const diff = computeDiff(
          existing as unknown as Record<string, unknown>,
          entry as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      } else {
        auditNewValue = entry as unknown as Record<string, unknown>;
      }
      try {
        await updateUnitConfig(entry);
        const idx = this.unitConfigs.findIndex((u) => u.id === entry.id);
        if (idx !== -1) this.unitConfigs[idx] = entry;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_UNIT_CONFIG_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update unit config';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'careUnits',
            resourceId: entry.id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Expiry Rules ──────────────────────────────────────────────────────

    async ensureExpiryRules() {
      if (this.expiryRulesFetched || this.expiryRulesLoading) return;
      this.expiryRulesLoading = true;
      try {
        this.expiryRules = await fetchExpiryRules('MILK');
        this.expiryRulesFetched = true;
      } finally {
        this.expiryRulesLoading = false;
      }
    },

    async addExpiryRule(rule: Omit<ExpiryRuleEntry, 'id'>) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const created = await addExpiryRule(rule, 'MILK');
        this.expiryRules.push(created);
        auditStatus = 'SUCCESS';
        auditResourceId = created.id;
        return created;
      } catch (err) {
        auditErrorCode = 'ADD_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to add expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'CREATE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: auditResourceId,
            isPhiAccess: false,
            newValue: rule as unknown as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async updateExpiryRule(rule: ExpiryRuleEntry) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.expiryRules.find((r) => r.id === rule.id);
      if (existing) {
        const diff = computeDiff(
          existing as unknown as Record<string, unknown>,
          rule as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      } else {
        auditNewValue = rule as unknown as Record<string, unknown>;
      }
      try {
        await updateExpiryRule(rule);
        const idx = this.expiryRules.findIndex((r) => r.id === rule.id);
        if (idx !== -1) this.expiryRules[idx] = rule;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: rule.id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async deleteExpiryRule(id: string) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.expiryRules.find((r) => r.id === id);
      if (existing) auditOldValue = existing as unknown as Record<string, unknown>;
      try {
        await deleteExpiryRule(id);
        this.expiryRules = this.expiryRules.filter((r) => r.id !== id);
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'DELETE_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to delete expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    /**
     * Activates/deactivates the Deep Frozen state rule for a given milk origin
     * (Mother Milk/Donor Milk/Donor Pasteurised Milk) without touching any other
     * field on the rule.
     */
    async toggleExpiryRuleActive(id: string, isActive: boolean) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.expiryRules.find((r) => r.id === id);
      try {
        await setExpiryRuleActive(id, isActive);
        const idx = this.expiryRules.findIndex((r) => r.id === id);
        if (idx !== -1) this.expiryRules[idx] = { ...this.expiryRules[idx], isActive };
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'TOGGLE_EXPIRY_RULE_ACTIVE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to toggle expiry rule state';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: id,
            isPhiAccess: false,
            oldValue: existing ? { isActive: existing.isActive } : undefined,
            newValue: { isActive },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Feed Expiry Rules ─────────────────────────────────────────────────

    async ensureFeedExpiryRules() {
      if (this.feedExpiryRulesFetched) return;
      this.feedExpiryRulesLoading = true;
      try {
        this.feedExpiryRules = await fetchExpiryRules('FEED');
        this.feedExpiryRulesFetched = true;
      } finally {
        this.feedExpiryRulesLoading = false;
      }
    },

    async addFeedExpiryRule(rule: Omit<ExpiryRuleEntry, 'id'>) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const created = await addExpiryRule(rule, 'FEED');
        this.feedExpiryRules.push(created);
        auditStatus = 'SUCCESS';
        auditResourceId = created.id;
        return created;
      } catch (err) {
        auditErrorCode = 'ADD_FEED_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to add feed expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'CREATE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: auditResourceId,
            isPhiAccess: false,
            newValue: rule as unknown as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async updateFeedExpiryRule(rule: ExpiryRuleEntry) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.feedExpiryRules.find((r) => r.id === rule.id);
      if (existing) {
        const diff = computeDiff(
          existing as unknown as Record<string, unknown>,
          rule as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      } else {
        auditNewValue = rule as unknown as Record<string, unknown>;
      }
      try {
        await updateExpiryRule(rule);
        const idx = this.feedExpiryRules.findIndex((r) => r.id === rule.id);
        if (idx !== -1) this.feedExpiryRules[idx] = rule;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_FEED_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update feed expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: rule.id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async deleteFeedExpiryRule(id: string) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.feedExpiryRules.find((r) => r.id === id);
      if (existing) auditOldValue = existing as unknown as Record<string, unknown>;
      try {
        await deleteExpiryRule(id);
        this.feedExpiryRules = this.feedExpiryRules.filter((r) => r.id !== id);
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'DELETE_FEED_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to delete feed expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Formula Expiry Rules ──────────────────────────────────────────────

    async ensureFormulaExpiryRules() {
      if (this.formulaExpiryRulesFetched) return;
      this.formulaExpiryRulesLoading = true;
      try {
        this.formulaExpiryRules = await fetchExpiryRules('FORMULA');
        this.formulaExpiryRulesFetched = true;
      } finally {
        this.formulaExpiryRulesLoading = false;
      }
    },

    async updateFormulaExpiryRule(rule: ExpiryRuleEntry) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.formulaExpiryRules.find((r) => r.id === rule.id);
      if (existing) {
        const diff = computeDiff(
          existing as unknown as Record<string, unknown>,
          rule as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      } else {
        auditNewValue = rule as unknown as Record<string, unknown>;
      }
      try {
        await updateExpiryRule(rule);
        const idx = this.formulaExpiryRules.findIndex((r) => r.id === rule.id);
        if (idx !== -1) this.formulaExpiryRules[idx] = rule;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'UPDATE_FORMULA_EXPIRY_RULE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update formula expiry rule';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'milkExpiryRules',
            resourceId: rule.id,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Roles & Permissions ───────────────────────────────────────────────

    async ensureRolesPermissions() {
      if (this.rolesPermissionsFetched) return;
      this.rolesPermissionsLoading = true;
      try {
        this.rolesPermissions = await fetchRolesPermissions();
        this.rolesPermissionsFetched = true;
      } finally {
        this.rolesPermissionsLoading = false;
      }
    },

    async ensureRolesPermissionsDefaults() {
      if (this.rolesPermissionsDefaultsFetched) return;
      try {
        this.rolesPermissionsDefaults = await fetchRolesPermissionsDefaults();
        this.rolesPermissionsDefaultsFetched = true;
      } catch {
        // Non-fatal: defaults may not be available; UI degrades gracefully
      }
    },

    async saveRolesPermissions(roles: RolePermissionEntry[]) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      const { oldValue: auditOldValue, newValue: auditNewValue } = computeRolePermissionsDiff(this.rolesPermissions, roles);
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        await saveRolesPermissions(roles);
        this.rolesPermissions = roles;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'SAVE_ROLES_PERMISSIONS_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to save roles & permissions';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'roleModuleMatrix',
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Devices ───────────────────────────────────────────────────────────

    async ensureDevices(deviceType?: string) {
      if (this.devicesFetched && this.devicesFetchedType === deviceType) return;
      this.devicesLoading = true;
      try {
        this.devices = await fetchDevices(deviceType);
        this.devicesFetched = true;
        this.devicesFetchedType = deviceType;
      } finally {
        this.devicesLoading = false;
      }
    },

    async assignDeviceCareUnit(deviceId: string, careUnitId: string) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.devices.find((d) => d.device_id === deviceId);
      if (existing) auditOldValue = { mutatedFields: ['care_unit_id'], care_unit_id: existing.care_unit_id ?? null };
      try {
        const updated = await updateDeviceCareUnit(deviceId, careUnitId);
        const idx = this.devices.findIndex((d) => d.device_id === deviceId);
        if (idx !== -1) this.devices[idx] = updated;
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'ASSIGN_DEVICE_CARE_UNIT_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to assign device care unit';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'devices',
            resourceId: deviceId,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: { mutatedFields: ['care_unit_id'], care_unit_id: careUnitId },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Approval & Compliance ─────────────────────────────────────────────

    async ensureApprovalCompliance() {
      if (this.approvalComplianceFetched) return;
      this.approvalComplianceLoading = true;
      try {
        this.approvalCompliance = await fetchApprovalCompliance();
        this.approvalComplianceFetched = true;
      } finally {
        this.approvalComplianceLoading = false;
      }
    },

    async saveApprovalCompliance(data: ApprovalComplianceFormData) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      if (this.approvalCompliance) {
        const diff = computeDiff(
          this.approvalCompliance as unknown as Record<string, unknown>,
          data as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      }
      try {
        await saveApprovalCompliance(data);
        this.approvalCompliance = data;
        auditStatus = 'SUCCESS';

        // A changed test validity window changes the screening verdict for donors
        // whose serology has not changed, so existing donors are recomputed here.
        // Best-effort: a failure must never fail the save — those donors are picked
        // up on their next serology change, as before.
        try {
          await reEvaluateDonorScreening();
        } catch { /* re-evaluation is advisory; the settings are already saved */ }
      } catch (err) {
        auditErrorCode = 'SAVE_APPROVAL_COMPLIANCE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to save approval & compliance';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'approvalCompliance',
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: (auditNewValue ?? data) as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Milk Formula Storage ──────────────────────────────────────────────

    async ensureMilkFormulaStorage(includeFormula?: boolean) {
      if (this.milkFormulaStorageFetched) return;
      this.milkFormulaStorageLoading = true;
      try {
        const shouldInclude = resolveShouldIncludeFormula(includeFormula);
        this.milkFormulaStorage = await fetchMilkFormulaStorage(shouldInclude);
        this.milkFormulaStorageFetched = true;
      } finally {
        this.milkFormulaStorageLoading = false;
      }
    },

    async saveMilkFormulaStorage(updated: MilkFormulaStorageFormData, includeFormula?: boolean) {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditNewValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      if (this.milkFormulaStorage) {
        const diff = computeDiff(
          this.milkFormulaStorage as unknown as Record<string, unknown>,
          updated as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;
      }
      try {
        const shouldIncludeFormula = resolveShouldIncludeFormula(includeFormula);
        await saveMilkFormulaStorage(this.milkFormulaStorage, updated, shouldIncludeFormula);
        // Reload to replace temp tag-N IDs with real UUIDs from the API
        this.milkFormulaStorageFetched = false;
        this.milkFormulaStorageLoading = true;
        try {
          this.milkFormulaStorage = await fetchMilkFormulaStorage(shouldIncludeFormula);
          this.milkFormulaStorageFetched = true;
          auditStatus = 'SUCCESS';
        } finally {
          this.milkFormulaStorageLoading = false;
        }
      } catch (err) {
        auditErrorCode = 'SAVE_MILK_FORMULA_STORAGE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to save milk formula storage';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'milkFormulaStorage',
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: (auditNewValue ?? updated) as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    /**
     * Update the cached milk/formula storage baseline without making any API calls.
     * Call this after each immediate chip add/remove so that the Save button's
     * diff-based `saveMilkFormulaStorage` always compares against the true
     * persisted state and never double-POSTs an already-saved entry.
     */
    syncMilkFormulaStorageLocally(data: MilkFormulaStorageFormData) {
      this.milkFormulaStorage = {
        milkLocations: [...data.milkLocations],
        formulaLocations: [...data.formulaLocations],
        pasteurizationDevices: [...data.pasteurizationDevices],
      };
    },

    // ── IDP Federation ────────────────────────────────────────────────────

    async ensureIdpFederation() {
      if (this.idpFederationFetched) return;
      this.idpFederationLoading = true;
      try {
        this.idpFederation = await listIdps();
        this.idpFederationFetched = true;
      } finally {
        this.idpFederationLoading = false;
      }
    },

    async refreshIdpFederation() {
      this.idpFederationFetched = false;
      await this.ensureIdpFederation();
    },

    // ── BridgeLink Assets ─────────────────────────────────────────────────

    async ensureBridgelinkAssets() {
      if (this.bridgelinkAssetsFetched) return;
      this.bridgelinkAssetsLoading = true;
      try {
        this.bridgelinkAssets = await fetchBridgelinkAssets();
        this.bridgelinkAssetsFetched = true;
      } finally {
        this.bridgelinkAssetsLoading = false;
      }
    },

    // ── Locale Languages ──────────────────────────────────────────────────

    async ensureLocaleLanguages() {
      if (this.localeLanguagesFetched) return;
      this.localeLanguagesLoading = true;
      try {
        this.localeLanguages = await fetchAvailableLanguages();
        this.localeLanguagesFetched = true;
      } finally {
        this.localeLanguagesLoading = false;
      }
    },

    // ── Locale Map (GET /map) ─────────────────────────────────────────────

    async ensureLocaleMap() {
      if (this.localeMapFetched) return;
      this.localeMapLoading = true;
      try {
        this.localeMap = await fetchLocaleMap();
        this.localeMapFetched = true;
      } finally {
        this.localeMapLoading = false;
      }
    },

    // ── Locale Keys (GET /keys) ───────────────────────────────────────────

    async ensureLocaleKeys() {
      if (this.localeKeysFetched) return;
      await this.loadLocaleKeys();
    },

    /**
     * Load locale keys with the current query filters.
     * Always hits the network (ignores localeKeysFetched).
     * When `append` is true, appends results to existing localeKeys (infinite scroll).
     */
    async loadLocaleKeys(query?: LocaleKeysQuery, append = false) {
      if (query) this.localeKeysQuery = query;
      this.localeKeysLoading = true;
      try {
        const authStore = useAuthStore();
        const lang = authStore.defaultLanguage || undefined;
        const mergedQuery = { limit: '50', page: '1', ...this.localeKeysQuery, ...(lang ? { lang } : {}) };
        const result = await fetchLocaleKeys(mergedQuery);
        if (append) {
          this.localeKeys = [...this.localeKeys, ...result.data];
        } else {
          this.localeKeys = result.data;
        }
        this.localeKeysPagination = result.pagination;
        this.localeKeysFetched = true;
      } finally {
        this.localeKeysLoading = false;
      }
    },

    /**
     * Load next page of locale keys (infinite scroll).
     * No-op if already on the last page or currently loading.
     */
    /**
     * Refreshes customizedLocaleKeysCount from GET /keys?is_customized=true —
     * one row requested, only the pagination total is used. Never throws:
     * the count is a convenience for the filter button, so a failure just
     * leaves the previous value in place.
     */
    async refreshCustomizedLocaleKeysCount() {
      try {
        const authStore = useAuthStore();
        const lang = authStore.defaultLanguage || undefined;
        const result = await fetchLocaleKeys({ is_customized: 'true', page: '1', limit: '1', ...(lang ? { lang } : {}) });
        this.customizedLocaleKeysCount = result.pagination.total;
      } catch { /* keep the last known count */ }
    },

    async loadNextLocaleKeysPage() {
      const { page, totalPages } = this.localeKeysPagination;
      if (page >= totalPages || this.localeKeysLoading) return;
      const nextPage = String(page + 1);
      await this.loadLocaleKeys({ ...this.localeKeysQuery, page: nextPage }, true);
    },

    // ── Save Locale Overrides ─────────────────────────────────────────────

    async saveLocaleOverrides(edits: Record<string, string>) {
      const authStore = useAuthStore();
      const lang = authStore.defaultLanguage || 'en';
      const overrides = Object.entries(edits).map(([key, custom_value]) => ({ key, custom_value }));
      if (!overrides.length) return;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const result = await saveOverrides({ tenantId: config.tenantId , lang }, { overrides });
        auditStatus = 'SUCCESS';
        // The response body already *is* the freshly-regenerated bundle —
        // apply it directly rather than re-fetching over HTTP (see
        // applyFreshBundle's docblock for why that re-fetch risks silently
        // reverting this save for up to 5 minutes). This is what makes the
        // edit visible everywhere $t()/t() render it, immediately — not just
        // in this page's own locale-key list below.
        applyFreshBundle(result.bundle);
        // Refresh keys so resolved_value reflects the saved custom values
        this.localeKeysFetched = false;
        await this.loadLocaleKeys();
        await this.refreshCustomizedLocaleKeysCount();
      } catch (err) {
        auditErrorCode = 'SAVE_LOCALE_OVERRIDES_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to save locale overrides';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'localeOverrides',
            isPhiAccess: false,
            newValue: { mutatedFields: ['overrides'], lang, overrides },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Reset Locale Override ─────────────────────────────────────────────

    async resetLocaleOverride(key: string) {
      const tenantId = config.tenantId;
      if (!tenantId) throw new Error('VITE_TENANT_ID is not configured');
      const authStore = useAuthStore();
      const lang = authStore.defaultLanguage || 'en';
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const result = await deleteOverride(key, { tenantId, lang });
        auditStatus = 'SUCCESS';
        // Same reasoning as saveLocaleOverrides: apply the response's
        // already-fresh bundle directly rather than a separate re-fetch
        // that can be served from cache for up to 5 minutes.
        applyFreshBundle(result.bundle);
        // Refresh keys so resolved_value is restored
        this.localeKeysFetched = false;
        await this.loadLocaleKeys();
        await this.refreshCustomizedLocaleKeysCount();
      } catch (err) {
        auditErrorCode = 'RESET_LOCALE_OVERRIDE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to reset locale override';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'localeOverrides',
            isPhiAccess: false,
            oldValue: { key },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Reset All Locale Overrides ────────────────────────────────────────

    /**
     * Reverts every customized key to its standard translation (custom_value
     * → NULL) in one request. Returns how many were reset (0 when nothing
     * was customized). Applies the response bundle the same way
     * resetLocaleOverride does.
     */
    async resetAllLocaleOverrides(): Promise<number> {
      const tenantId = config.tenantId;
      if (!tenantId) throw new Error('VITE_TENANT_ID is not configured');
      const authStore = useAuthStore();
      const lang = authStore.defaultLanguage || 'en';
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let processed = 0;
      try {
        const result = await resetAllOverrides({ tenantId, lang });
        auditStatus = 'SUCCESS';
        processed = result.processed;
        applyFreshBundle(result.bundle);
        this.localeKeysFetched = false;
        await this.loadLocaleKeys();
        await this.refreshCustomizedLocaleKeysCount();
        return processed;
      } catch (err) {
        auditErrorCode = 'RESET_ALL_LOCALE_OVERRIDES_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to reset all locale overrides';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'localeOverrides',
            isPhiAccess: false,
            oldValue: { scope: 'ALL', lang, count: processed },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Find & Replace Locale Terms ───────────────────────────────────────

    /** Read-only: keys whose translation contains the find term. No audit event — nothing changes. */
    async previewLocaleReplace(match: LocaleReplacePreviewBody) {
      const tenantId = config.tenantId;
      if (!tenantId) throw new Error('VITE_TENANT_ID is not configured');
      const authStore = useAuthStore();
      const lang = authStore.defaultLanguage || 'en';
      return previewReplace({ tenantId, lang }, match);
    },

    /**
     * Replaces the term in `body.keys`. Applies the response bundle the same
     * way saveLocaleOverrides does, and returns the result.
     */
    async applyLocaleReplace(body: LocaleReplaceApplyBody) {
      const tenantId = config.tenantId;
      if (!tenantId) throw new Error('VITE_TENANT_ID is not configured');
      const authStore = useAuthStore();
      const lang = authStore.defaultLanguage || 'en';
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let processedKeys: string[] = [];
      try {
        const result = await applyReplace({ tenantId, lang }, body);
        auditStatus = 'SUCCESS';
        const skippedKeys = new Set(result.skipped.map((s) => s.key));
        processedKeys = [...new Set(body.keys)].filter((key) => !skippedKeys.has(key));
        // Same reasoning as saveLocaleOverrides: apply the response's
        // already-fresh bundle rather than a re-fetch that can be stale.
        applyFreshBundle(result.bundle);
        this.localeKeysFetched = false;
        await this.loadLocaleKeys();
        await this.refreshCustomizedLocaleKeysCount();
        return result;
      } catch (err) {
        auditErrorCode = 'REPLACE_LOCALE_TERMS_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to replace locale terms';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'localeOverrides',
            isPhiAccess: false,
            newValue: {
              mutatedFields: ['overrides'],
              lang,
              findReplace: { find: body.find, replace: body.replace, matchCase: body.matchCase, wholeWord: body.wholeWord },
              keys: processedKeys,
            },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Import Locale Overrides (CSV) ─────────────────────────────────────

    async importLocaleOverrides(file: File) {
      const lang = 'en';
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let result: unknown;
      try {
        result = await importOverrides(config.tenantId, lang, file);
        auditStatus = 'SUCCESS';
        // Refresh keys after CSV import
        this.localeKeysFetched = false;
        await this.loadLocaleKeys();
        await this.refreshCustomizedLocaleKeysCount();
        return result;
      } catch (err) {
        auditErrorCode = 'IMPORT_LOCALE_OVERRIDES_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to import locale overrides';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'IMPORT',
            eventStatus: auditStatus,
            resourceType: 'localeOverrides',
            isPhiAccess: false,
            newValue: { fileName: file.name, lang },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── Sync Locale Bundle ────────────────────────────────────────────────

    async syncLocaleBundle(languageCode: string, isInitialSetup: boolean) {
      const tenantId = config.tenantId;
      if (!tenantId) throw new Error('VITE_TENANT_ID is not configured');
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let result: unknown;
      try {
        result = await syncBundle({ tenant_id: tenantId, language_code: languageCode as LocaleSyncBody['language_code'], is_initial_setup: isInitialSetup });
        auditStatus = 'SUCCESS';
        // Refresh keys after sync
        this.localeKeysFetched = false;
        await this.loadLocaleKeys();
        return result;
      } catch (err) {
        auditErrorCode = 'SYNC_LOCALE_BUNDLE_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to sync locale bundle';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'SYNC',
            eventStatus: auditStatus,
            resourceType: 'localeBundle',
            isPhiAccess: false,
            newValue: { languageCode, isInitialSetup },
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    // ── IDP Federation — mutating actions ─────────────────────────────────

    async createIdpProvider(payload: CreateIdpPayload): Promise<IdpProviderResponse> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      try {
        const result = await createIdp(payload);
        auditStatus = 'SUCCESS';
        auditResourceId = result.provider_name;
        return result;
      } catch (err) {
        auditErrorCode = 'CREATE_IDP_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to create identity provider';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'CREATE',
            eventStatus: auditStatus,
            resourceType: 'idpFederation',
            resourceId: auditResourceId,
            isPhiAccess: false,
            newValue: payload as unknown as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async updateIdpProvider(providerName: string, payload: UpdateIdpPayload): Promise<IdpProviderResponse> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.idpFederation.find((p) => p.provider_name === providerName);
      if (existing) {
        const diff = computeDiff(
          existing as unknown as Record<string, unknown>,
          payload as unknown as Record<string, unknown>,
        );
        auditOldValue = diff.oldValue;
      }
      try {
        const result = await updateIdp(providerName, payload);
        auditStatus = 'SUCCESS';
        return result;
      } catch (err) {
        auditErrorCode = 'UPDATE_IDP_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to update identity provider';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'UPDATE',
            eventStatus: auditStatus,
            resourceType: 'idpFederation',
            resourceId: providerName,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: payload as Record<string, unknown>,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },

    async deleteIdpProvider(providerName: string): Promise<void> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditOldValue: unknown;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const existing = this.idpFederation.find((p) => p.provider_name === providerName);
      if (existing) auditOldValue = existing as unknown as Record<string, unknown>;
      try {
        await deleteIdp(providerName);
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditErrorCode = 'DELETE_IDP_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Failed to delete identity provider';
        if (isAxiosError(err)) {
          const apiErr = err.response?.data?.error;
          auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
        }
        throw err;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'SETTINGS',
            eventType: 'DELETE',
            eventStatus: auditStatus,
            resourceType: 'idpFederation',
            resourceId: providerName,
            isPhiAccess: false,
            oldValue: auditOldValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch { /* Audit must never block the flow */ }
      }
    },
  },
});
